import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlatformAdmin, SystemStatus } from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import { formatDateTime, formatNumber } from '../../shared/format';
import { useSession } from '../../shared/session';
import {
  ActionsCell,
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  DefinitionList,
  Drawer,
  Empty,
  Field,
  Form,
  Input,
  Note,
  PageHead,
  Stats,
  Table,
  useAdminToast,
} from '../admin-kit';
import { wanneer } from './platform-format';

/** De fout van de API als tekst: de `detail` van een problem-antwoord, anders de algemene zin. */
function problemText(error: unknown, fallback: string): string {
  const detail = (error as { detail?: unknown } | null)?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
}

// --- Beheerders (MST-7) ------------------------------------------------------------------------

/**
 * Wie Master mag gebruiken. Een nieuwe beheerder krijgt een tijdelijk wachtwoord dat je hem zelf
 * bezorgt; er vertrekt geen mail. Bij de eerste aanmelding koppelt hij zijn authenticator-app
 * (FBK-12); zonder die tweede factor komt niemand in Master.
 */
export function PlatformAdminsPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const toast = useAdminToast();
  const [open, setOpen] = useState(false);

  const admins = useQuery({
    queryKey: ['platform', 'admins'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/admins');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: PlatformAdmin[] }).items;
    },
  });

  // De 2FA van een platformbeheerder terugzetten (FBK-12); hij schrijft zich opnieuw in.
  const resetMfa = useMutation({
    mutationFn: async (adminId: string) => {
      const { error } = await api.DELETE('/api/v1/platform/admins/{adminId}/mfa', {
        params: { path: { adminId } },
      });
      if (error) throw error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['platform', 'admins'] }),
  });

  const toggle = useMutation({
    mutationFn: async ({ id, disable }: { id: string; disable: boolean }) => {
      const params = { path: { adminId: id } };
      const { error } = disable
        ? await api.POST('/api/v1/platform/admins/{adminId}/disable', { params })
        : await api.POST('/api/v1/platform/admins/{adminId}/enable', { params });
      if (error) throw error;
      return disable;
    },
    onSuccess: async (disable) => {
      toast.show(t(disable ? 'master.admins.disabled' : 'master.admins.enabled'));
      await queryClient.invalidateQueries({ queryKey: ['platform', 'admins'] });
    },
  });

  return (
    <AdminPage>
      <PageHead
        title={t('master.admins.title')}
        sub={t('master.admins.sub')}
        actions={
          <Btn icon={<AdminIcon name="plus" size={16} />} onClick={() => setOpen(true)}>
            {t('master.admins.add')}
          </Btn>
        }
      />
      <Card>
        {toggle.isError ? (
          <CardPad>
            <Note tone="danger" role="alert">
              {problemText(toggle.error, t('errors.generic'))}
            </Note>
          </CardPad>
        ) : null}
        {admins.isPending ? (
          <LoadingState rows={3} label={t('common.loading')} />
        ) : admins.isError ? (
          <CardPad>
            <Note tone="danger" role="alert">
              {t('errors.generic')}
            </Note>
          </CardPad>
        ) : (admins.data?.length ?? 0) === 0 ? (
          <Empty icon={<AdminIcon name="people" size={24} />} title={t('master.admins.empty')} />
        ) : (
          <Table label={t('master.admins.title')}>
            <thead>
              <tr>
                <th>{t('master.admins.name')}</th>
                <th>{t('master.admins.email')}</th>
                <th>{t('master.admins.mfa')}</th>
                <th>{t('master.admins.lastLogin')}</th>
                <th>{t('master.admins.status')}</th>
                <th className="act">
                  <span className="a-sr">{t('master.people.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {admins.data?.map((admin) => (
                <tr key={admin.id} data-testid="platform-admin-row">
                  <td>
                    <span className="a-name">{admin.displayName}</span>
                  </td>
                  <td>{admin.email}</td>
                  <td>
                    {admin.mfa ? (
                      <Badge tone="ok">{t('master.admins.mfaOn')}</Badge>
                    ) : (
                      <Badge tone="attention">{t('master.admins.mfaOff')}</Badge>
                    )}
                  </td>
                  <td>{wanneer(admin.lastLoginAt, t)}</td>
                  <td>
                    {admin.disabledAt ? (
                      <Badge>{t('master.admins.inactive')}</Badge>
                    ) : (
                      <Badge tone="ok">{t('master.admins.active')}</Badge>
                    )}
                  </td>
                  <ActionsCell>
                    {admin.mfa ? (
                      <Btn
                        variant="ghost"
                        size="sm"
                        testId="platform-admin-mfa-reset"
                        disabled={resetMfa.isPending}
                        onClick={() => {
                          if (window.confirm(t('mfa.resetConfirm'))) resetMfa.mutate(admin.id);
                        }}
                      >
                        {t('mfa.reset')}
                      </Btn>
                    ) : null}
                    <Btn
                      variant="ghost"
                      size="sm"
                      disabled={toggle.isPending}
                      onClick={() => toggle.mutate({ id: admin.id, disable: !admin.disabledAt })}
                    >
                      {admin.disabledAt ? t('master.admins.enable') : t('master.admins.disable')}
                    </Btn>
                  </ActionsCell>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      {open ? (
        <NewAdminDrawer
          onClose={() => setOpen(false)}
          onDone={(name) => {
            setOpen(false);
            toast.show(t('master.admins.added', { name }));
          }}
        />
      ) : null}
      {toast.element}
    </AdminPage>
  );
}

function NewAdminDrawer({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (name: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const klaar = name.trim().length >= 2 && email.includes('@') && password.length >= 12;

  const create = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/platform/admins', {
        body: { displayName: name.trim(), email: email.trim(), password } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform', 'admins'] });
      onDone(name.trim());
    },
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title={t('master.admins.add')}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn disabled={!klaar || create.isPending} onClick={() => create.mutate()}>
            {t('master.admins.add')}
          </Btn>
        </>
      }
    >
      <Form onSubmit={() => klaar && create.mutate()}>
        <Note tone="info">{t('master.admins.addIntro')}</Note>
        {create.isError ? (
          <Note tone="danger" role="alert">
            {problemText(create.error, t('errors.generic'))}
          </Note>
        ) : null}
        <Field label={t('master.admins.name')}>
          {(props) => (
            <Input {...props} value={name} onChange={(event) => setName(event.target.value)} />
          )}
        </Field>
        <Field label={t('master.admins.email')}>
          {(props) => (
            <Input
              {...props}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('master.admins.password')} hint={t('master.admins.passwordHint')}>
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </Field>
      </Form>
    </Drawer>
  );
}

// --- Systeem (MST-8) ---------------------------------------------------------------------------

/** De systeemstatus, elke 30 seconden vernieuwd terwijl de pagina open staat. */
export function PlatformSystemPage() {
  const { t } = useTranslation();
  const { api } = useSession();

  const status = useQuery({
    queryKey: ['platform', 'system'],
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/system');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as SystemStatus;
    },
  });

  const data = status.data;
  return (
    <AdminPage>
      <PageHead
        title={t('master.system.title')}
        sub={
          data
            ? t('master.system.checked', { time: formatDateTime(data.checkedAt) })
            : t('master.system.sub')
        }
        actions={
          <Btn
            variant="sec"
            icon={<AdminIcon name="rotate" size={16} />}
            disabled={status.isFetching}
            onClick={() => void status.refetch()}
          >
            {t('master.system.refresh')}
          </Btn>
        }
      />
      {status.isPending ? (
        <LoadingState rows={5} label={t('common.loading')} />
      ) : status.isError || !data ? (
        <Note tone="danger" role="alert">
          {t('errors.generic')}
        </Note>
      ) : (
        <>
          <Card>
            <CardPad>
              <Stats
                label={t('master.system.title')}
                items={[
                  {
                    label: t('master.system.database'),
                    value: data.database.ok ? t('master.system.ok') : t('master.system.down'),
                    sub: t('master.system.latency', { ms: data.database.latencyMs }),
                    testId: 'system-database',
                  },
                  {
                    label: t('master.system.worker'),
                    value: data.outbox.workerHealthy
                      ? t('master.system.ok')
                      : t('master.system.stuck'),
                    sub: data.outbox.oldestPendingAt
                      ? t('master.system.oldestPending', {
                          time: formatDateTime(data.outbox.oldestPendingAt),
                        })
                      : t('master.system.nothingPending'),
                    testId: 'system-worker',
                  },
                  {
                    label: t('master.system.pending'),
                    value: formatNumber(data.outbox.pending),
                    sub: t('master.system.dispatched', { count: data.outbox.dispatchedLastHour }),
                  },
                  {
                    label: t('master.system.deadLetters'),
                    value: formatNumber(data.outbox.deadLetters),
                    sub: t('master.system.deadLettersSub'),
                  },
                ]}
              />
            </CardPad>
          </Card>

          <Card>
            <CardHead title={t('master.system.mail')} />
            <CardPad className="a-stack">
              {data.mail.allowedRecipients.length > 0 ? (
                <Note tone="attention">{t('master.system.allowlistOn')}</Note>
              ) : (
                <Note tone="info">{t('master.system.allowlistOff')}</Note>
              )}
              <DefinitionList
                items={[
                  [
                    t('master.system.smtp'),
                    data.mail.smtpConfigured
                      ? t('master.system.smtpOn')
                      : t('master.system.smtpOff'),
                  ],
                  [t('master.system.from'), data.mail.from],
                  [
                    t('master.system.allowedRecipients'),
                    data.mail.allowedRecipients.join(', ') || t('master.system.everyone'),
                  ],
                  [
                    t('master.system.allowedTenants'),
                    data.mail.allowedTenantIds.length > 0
                      ? formatNumber(data.mail.allowedTenantIds.length)
                      : t('master.system.none'),
                  ],
                ]}
              />
              <p className="a-meta">{t('master.system.allowlistHint')}</p>
            </CardPad>
          </Card>

          <Card>
            <CardHead title={t('master.system.problems')} count={data.problems.length} />
            {data.problems.length === 0 ? (
              <Empty
                icon={<AdminIcon name="checkCircle" size={24} />}
                title={t('master.system.noProblems')}
              />
            ) : (
              <Table label={t('master.system.problems')}>
                <thead>
                  <tr>
                    <th>{t('master.system.when')}</th>
                    <th>{t('master.system.event')}</th>
                    <th className="num">{t('master.system.attempts')}</th>
                    <th>{t('master.system.error')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.problems.map((problem) => (
                    <tr key={problem.id}>
                      <td>{formatDateTime(problem.occurredAt)}</td>
                      <td className="a-mono">
                        {problem.eventName}{' '}
                        {problem.deadLetter ? (
                          <Badge tone="danger">{t('master.system.dead')}</Badge>
                        ) : null}
                      </td>
                      <td className="num">{problem.attempts}</td>
                      <td className="a-meta">{problem.lastError ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          <Card>
            <CardHead title={t('master.system.environment')} />
            <CardPad>
              <DefinitionList
                items={[
                  [t('master.system.env'), data.environment],
                  [t('master.system.startedAt'), formatDateTime(data.apiStartedAt)],
                  [t('master.system.node'), data.nodeVersion],
                  [t('master.system.modules'), data.modules.join(', ')],
                ]}
              />
            </CardPad>
          </Card>
        </>
      )}
    </AdminPage>
  );
}
